import { Box, Button } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { MAX_TEXT_LENGTH } from "@outschool/text";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Space } from "@outschool/ui-legacy-component-library";
import React from "react";

import { useReplyToContext } from "../../../providers/ReplyToProvider";
import EditableMessageContent, {
  EditableMessageContentSubmitType,
} from "../../EditableMessageContent";

import type { AttachmentFragmentFragment } from "../../..";

type MentionsDataItem = {
  id: string;
  display: string;
};

type onSubmitProps = {
  text: string;
  attachments?: AttachmentFragmentFragment[] | null;
  video?: AttachmentFragmentFragment | null;
  mentions?: MentionsDataItem[] | null;
};

export interface AddClassPostCommentInputProps {
  placeholder?: string;
  mentionNames?: MentionsDataItem[];
  onSubmit(props: onSubmitProps): Promise<void>;
}

export default function AddClassPostCommentInput({
  placeholder,
  mentionNames,
  onSubmit,
}: AddClassPostCommentInputProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassPostComment\\AddClassPostCommentInput",
  );
  const { messageContents, clearReplyTo } = useReplyToContext();
  const handleCommentSubmit = async ({
    text,
    attachments,
    video,
    mentions,
  }: EditableMessageContentSubmitType) => {
    try {
      await onSubmit({
        text,
        attachments,
        video,
        mentions,
      });
      clearReplyTo();
      return true;
    } catch (error) {
      return false;
    }
  };
  return (
    <Box
      data-test-id="AddClassPostComment"
      sx={{
        width: "100%",
      }}
    >
      <EditableMessageContent
        collapsedUntilFocused
        inClassContext
        placeholder={placeholder ?? `Write a comment`}
        messageContent={messageContents}
        onSubmit={handleCommentSubmit}
        classMentionsData={mentionNames}
        maxLength={MAX_TEXT_LENGTH}
        renderButton={({ submit, disabled, reset }) => {
          return (
            <Box
              flex
              sx={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <TrackedButton
                variant="contained"
                disabled={disabled}
                trackingName="send_comment"
                onClick={async () => {
                  const success = await submit();
                  if (success) {
                    reset();
                  }
                }}
              >
                {t`Send`}
              </TrackedButton>
              <Space x="medium" />
              <Button
                variant="link"
                onClick={() => {
                  reset();
                }}
              >
                {t`Cancel`}
              </Button>
            </Box>
          );
        }}
        // eslint-disable-next-line
        autoFocus={false}
        expandedRows={10}
      />
    </Box>
  );
}
